import {useEffect, useRef, useState} from "react";
import L from "leaflet";

import createWasteIconJS from "components/waste-icons/svgs/createWasteIconJS";
import getMarkerFontAwesomeJS from "components/waste-icons/svgs/getMarkerFontAwesomeJS";
import Loader from "components/loaders/Loader";
import {CARTO_TILE_URL, CARTO_TILE_OPTIONS} from "core/mapsConfig";
import useCustomTranslation from "core/customTranslation";
import translations from "./translations";

const FormFleetRouteMap = ({center, checkpoints = [], geojson = {}, telemetry = [], highlightedPoint, styleMap, wasteType, truckUpdaterRef}) => {
    const {t: _CT} = useCustomTranslation(translations);
    const mapRef = useRef(null);
    const assetsLayerRef = useRef(L.layerGroup([]));
    const plannedRouteRef = useRef(L.featureGroup([]));
    const telemetryRouteRef = useRef(L.featureGroup([]));
    const highlightLayerRef = useRef(L.layerGroup([]));
    const markersRef = useRef({});
    const truckMarkerRef = useRef(null);
    const lastPosRef = useRef(null);
    const mapReady = useRef(false);
    const fittedRef = useRef(false);
    const pannedPointRef = useRef(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!center || mapRef.current) return;
        mapRef.current = L.map("fleet-route-map").setView(center, 13);
        L.tileLayer(CARTO_TILE_URL, CARTO_TILE_OPTIONS).addTo(mapRef.current);
        assetsLayerRef.current.addTo(mapRef.current);
        plannedRouteRef.current.addTo(mapRef.current);
        telemetryRouteRef.current.addTo(mapRef.current);
        highlightLayerRef.current.addTo(mapRef.current);
        mapReady.current = true;
        setReady(true);
        paintAll();
    }, [center]);

    const paintAll = () => {
        addAssetsMarkers();
        paintPlannedRoute();
        paintTelemetryRoute();
        // solo se encuadra la primera vez; al refrescar se respeta el zoom del encargado
        if (fittedRef.current) return;
        const bounds = fitBounds();
        if (bounds && mapRef.current) {
            mapRef.current.fitBounds(bounds);
            fittedRef.current = true;
        }
    };

    const addAssetsMarkers = () => {
        if (!mapRef.current) return;
        assetsLayerRef.current.clearLayers();
        markersRef.current = {};
        checkpoints.forEach((point, index) => {
            if (point.lat == null || point.lng == null) return;
            let icon, name;
            if (point?.mooch) {
                icon = createWasteIconJS("fl", 100);
                name = point.uid;
            } else if (point.island_id) {
                // Isla: el operario necesita ver el nombre y los UID de los
                // contenedores que cuelgan de ella. Ojo: una isla también llega
                // con `is_asset: true`, así que esta rama va antes que la del
                // contenedor suelto.
                icon = createWasteIconJS(point.waste_type || wasteType, point.filling || point.island_filling);
                const uids = String(point.uids || "")
                    .split(",")
                    .map((u) => u.trim())
                    .filter(Boolean);
                name =
                    `<strong>${_CT("island")}: ${point.uid || point.waypoint_name || ""}</strong>` +
                    (uids.length ? `<br/>${_CT("uids")}: ${uids.join(", ")}` : "");
            } else if (point.is_asset) {
                icon = createWasteIconJS(point.waste_type || wasteType, point.filling || point.island_filling);
                name = "UID: " + (point.uid || "");
            } else if (point.waypoint_type === "s") {
                // Waypoint fantasma (Spectre): incidencia del conductor de un
                // contenedor que no estaba en la ruta. No se ve en el listado de
                // referencias, pero sí aquí, con aviso para el operario.
                icon = getMarkerFontAwesomeJS("triangle-exclamation");
                name = point.observations
                    ? `${point.waypoint_name || "Incidencia"}: ${point.observations}`
                    : point.waypoint_name || "Incidencia del conductor";
            } else {
                icon = getMarkerFontAwesomeJS(point.waypoint_icon);
                name = point.waypoint_name;
            }
            const marker = L.marker([point.lat, point.lng], {icon}).addTo(mapRef.current).bindPopup(name);
            assetsLayerRef.current.addLayer(marker);
            const idKey = String(point.id ?? point.asset_id ?? point.waypoint_id ?? point.uid ?? index);
            markersRef.current[idKey] = marker;
        });
    };

    const paintPlannedRoute = () => {
        plannedRouteRef.current.clearLayers();
        if (!geojson?.geometry?.coordinates?.length) return;
        geojson.geometry.coordinates.forEach((steps) => {
            const coords = steps.map(([lng, lat]) => [lat, lng]);
            if (!coords.length) return;
            plannedRouteRef.current.addLayer(L.polyline(coords, {color: "#3388ff", opacity: 0.5, weight: 3}));
        });
    };

    const paintTelemetryRoute = () => {
        telemetryRouteRef.current.clearLayers();
        if (!telemetry?.length) return;
        const coords = telemetry.map((p) => [p.lat, p.lng]).filter((c) => c[0] != null && c[1] != null);
        if (coords.length < 2) return;
        telemetryRouteRef.current.addLayer(L.polyline(coords, {color: "#22c55e", opacity: 0.9, weight: 4}));
        const start = coords[0];
        const end = coords[coords.length - 1];
        telemetryRouteRef.current.addLayer(
            L.circleMarker(start, {radius: 6, color: "#22c55e", fillColor: "#22c55e", fillOpacity: 1})
        );
        telemetryRouteRef.current.addLayer(
            L.circleMarker(end, {radius: 6, color: "#166534", fillColor: "#166534", fillOpacity: 1})
        );
    };

    const fitBounds = () => {
        const groups = [assetsLayerRef.current, plannedRouteRef.current, telemetryRouteRef.current];
        const points = [];
        groups.forEach((g) => {
            g.getLayers().forEach((l) => {
                if (l?.getLatLng?.()) points.push(l.getLatLng());
                if (l?.getLatLngs?.()) {
                    try {
                        const latlngs = l.getLatLngs();
                        if (latlngs[0] && Array.isArray(latlngs[0])) {
                            latlngs.forEach((ll) => {
                                if (ll?.lat != null && ll?.lng != null) points.push(ll);
                            });
                        } else if (latlngs.length) {
                            points.push(...latlngs);
                        }
                    } catch (e) {}
                }
            });
        });
        if (!points.length) return null;
        return L.latLngBounds(points);
    };

    // va antes que el resalte: así el resalte se aplica sobre los marcadores ya repintados
    useEffect(() => {
        if (mapReady.current) paintAll();
    }, [checkpoints, geojson, telemetry]);

    useEffect(() => {
        if (!mapRef.current) return;
        highlightLayerRef.current.clearLayers();
        if (!highlightedPoint) {
            pannedPointRef.current = null;
            Object.values(markersRef.current).forEach((m) => {
                try {
                    const el = m.getElement();
                    if (el) el.style.opacity = "1";
                } catch (e) {}
            });
            return;
        }
        const id = String(highlightedPoint);
        const activeMarker = markersRef.current[id];
        Object.entries(markersRef.current).forEach(([key, m]) => {
            try {
                const el = m.getElement();
                if (el) el.style.opacity = key === id ? "1" : "0.2";
            } catch (e) {}
        });
        if (!activeMarker) return;
        try {
            activeMarker.setZIndexOffset(1000);
        } catch (e) {}
        // centra solo al cambiar de punto, no al repintar por un refresco
        if (pannedPointRef.current === id) return;
        pannedPointRef.current = id;
        try {
            mapRef.current.panTo(activeMarker.getLatLng());
        } catch (e) {}
    }, [highlightedPoint, checkpoints]);

    useEffect(() => {
        if (!truckUpdaterRef) return;
        truckUpdaterRef.current = (lat, lng) => {
            if (lat == null || lng == null) return;
            lastPosRef.current = [lat, lng];
            const map = mapRef.current;
            if (!map) return;
            if (!truckMarkerRef.current) {
                truckMarkerRef.current = L.marker([lat, lng], {
                    icon: getMarkerFontAwesomeJS("truck"),
                    zIndexOffset: 1200,
                }).addTo(map);
            } else {
                truckMarkerRef.current.setLatLng([lat, lng]);
            }
        };
        return () => {
            if (truckUpdaterRef) truckUpdaterRef.current = null;
        };
    }, [truckUpdaterRef]);

    useEffect(() => {
        if (ready && lastPosRef.current) {
            truckUpdaterRef?.current?.(lastPosRef.current[0], lastPosRef.current[1]);
        }
    }, [ready, truckUpdaterRef]);

    return (
        <div style={{height: "100%", width: "100%"}}>
            <div id="fleet-route-map" style={{height: styleMap?.height || "55vh", width: "100%"}}>
                <Loader show={!ready} />
            </div>
        </div>
    );
};
export default FormFleetRouteMap;
